import clsx from 'clsx'
import { observer } from 'mobx-react-lite'
import { motion } from 'motion/react'
import { store } from '../../stores/store'
import { buildCharacterContinueUrl } from '../../utils/characterShareUrl'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'
import ButtonIcon from '../ui/buttons/ButtonIcon'
import IconClose from '../ui/icons/IconClose'
import TextHeadline from '../ui/texts/TextHeadline'
import QrCode from '../ui/QrCode'

type ContinueOnPhoneProps = {
    onClose: () => void
    shareUrl?: string
    className?: string
}

const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2, ease: 'easeOut' as const } },
    exit: { opacity: 0, transition: { duration: 0.2, ease: 'easeIn' as const } },
}

const cardVariants = {
    hidden: { opacity: 0, scale: 0.8, y: -20 },
    visible: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: {
            type: 'spring' as const,
            stiffness: 380,
            damping: 18,
            mass: 0.8,
        },
    },
    exit: { opacity: 0, scale: 0.95, transition: { duration: 0.15, ease: 'easeIn' as const } },
}

const ContinueOnPhone = observer(function ContinueOnPhone({
    onClose,
    shareUrl,
    className,
}: ContinueOnPhoneProps) {
    const isStele = useSteleBreakpoint()
    const refDocumentId = store.entryReferrerLandingMatch?.mapping?.documentId ?? null
    const resolvedShareUrl = shareUrl ?? buildCharacterContinueUrl(refDocumentId)

    return (
        <motion.div
            className={clsx(
                'z-100 fixed inset-0 flex justify-center items-end bg-black/55 backdrop-blur-sm pointer-events-auto',
                className,
            )}
            variants={overlayVariants}
            initial='hidden'
            animate='visible'
            exit='exit'
        >
            <button
                type='button'
                aria-label='Schließen'
                className='absolute inset-0 cursor-default'
                onClick={onClose}
            />

            <div
                className={clsx(
                    'relative',
                    'mb-32',
                    'stele:mb-128',
                )}
            >
                <motion.div
                    className={clsx(
                        'flex flex-col justify-center items-center drop-shadow-2-black-stroke bg-white-stroke-even-darker border-black-stroke text-center pattern-background',
                        'gap-4 px-8 pt-16 pb-10 border-2 rounded-[48px] max-w-180',
                        'stele:gap-8 stele:px-16 stele:pt-0 stele:pb-24 stele:border-4 stele:rounded-[128px] stele:max-w-none stele:w-[1648px]',
                    )}
                    variants={cardVariants}
                >
                    <div className='flex flex-col items-center'>
                        <div
                            className={clsx(
                                'absolute flex flex-col items-center',
                                '-top-14',
                                'stele:-top-80',
                            )}
                        >
                            <TextHeadline
                                fontSize={isStele ? 160 : 56}
                                leading={-0.05}
                                tracking='-2%'
                                colorPreset='red'
                            >
                                Weiter geht&apos;s
                            </TextHeadline>
                            <TextHeadline
                                fontSize={isStele ? 160 : 56}
                                leading={1}
                                tracking='-2%'
                                colorPreset='red'
                            >
                                auf Deinem Handy
                            </TextHeadline>
                        </div>
                    </div>

                    <p
                        className={clsx(
                            'text-white-stroke-lightest font-nunito font-bold text-center tracking-[-2%]',
                            'text-[18px] leading-6 max-w-72',
                            'stele:text-[64px] stele:leading-22 stele:max-w-none stele:mt-32',
                        )}
                        style={{ textShadow: '0 2px 0 #06487C' }}
                    >
                        Scanne den Code und spiel gleich weiter.
                    </p>

                    <div
                        className={clsx(
                            'flex justify-center items-end',
                            'flex-col gap-4',
                            'stele:flex-row stele:gap-12 stele:items-center stele:pr-32',
                        )}
                    >
                        <img
                            src='/images/phone-hand.png'
                            alt=''
                            className={clsx(
                                'object-contain shrink-0',
                                'w-36 h-45',
                                'stele:w-[614px] stele:h-[768px] stele:-mb-24',
                            )}
                        />

                        <div
                            className={clsx(
                                'flex justify-center items-center bg-white border-30 border-white shrink-0',
                                'p-4 rounded-4xl',
                                'stele:p-0 stele:rounded-[48px]',
                            )}
                        >
                            <QrCode
                                value={resolvedShareUrl}
                                size={isStele ? 500 : 200}
                                color='#0E0622'
                                background='#FFFFFF'
                            />
                        </div>
                    </div>
                </motion.div>

                <div
                    className={clsx(
                        'left-1/2 absolute -translate-x-1/2',
                        '-bottom-6',
                        'stele:-bottom-24',
                    )}
                >
                    <ButtonIcon
                        color='red'
                        variant='standalone'
                        popupCloseSound
                        icon={<IconClose width='100%' height='100%' />}
                        onClick={onClose}
                        aria-label='Schließen'
                    />
                </div>
            </div>
        </motion.div>
    )
})

export default ContinueOnPhone

